import * as React from 'react'

import EmptyState from '../empty_state'
import { SkillPerformanceInterface, } from '../interfaces'
import { TABLE_WIDTH_PROPS, } from '../shared'
import { EXTRA_SMALL_ICON_BASE_SRC, DataTable, Tooltip, Spinner, DataTableChip, } from '../../../../../Shared/index'
import useStickyTableHeader from '../../../../../Shared/hooks/useStickyTableHeader'
import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths'

const helpIcon = <img alt="Question mark inside of circle" src={`${EXTRA_SMALL_ICON_BASE_SRC}/help.svg`} />

interface BySkillPropsInterface {
  skillPerformances: SkillPerformanceInterface[],
  additionalRequestRunning: boolean
}

const BySkill = ({ skillPerformances, additionalRequestRunning, }: BySkillPropsInterface) => {
  const containerRef = React.useRef<HTMLElement>(null);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);

  const columnWidths = useScaledTableColumnWidths({
    columnWidths: [
      { width: 292, minWidth: 292 },
      { width: 224 },
      { width: 224 },
      { width: 224 },
      { width: 224 },
    ],
    containerRef,
    ...TABLE_WIDTH_PROPS,
  });

  const headers = [
    {
      name: 'Skill',
      attribute: 'name',
      width: columnWidths['292w-292min'],
      noTooltip: true,
      rowSectionClassName: 'name border-right',
      renderCellsAsHeaders: true,
    },
    {
      name: (
        <span className="header-with-tooltip">
          Full Proficiency
          <Tooltip
            skipPositioning={true}
            tooltipText="The student answered all questions for this skill correctly on at least one Pre or Post diagnostic completed in the selected timeframe."
            tooltipTriggerText={helpIcon}
          />
        </span>
      ),
      attribute: 'fullProficiency',
      width: columnWidths['224w'],
      headerClassName: 'eight-px-right-padding',
      rowSectionClassName: 'eight-px-right-padding show-overflow full-proficiency',
      textAlign: 'left',
      noTooltip: true,
      ariaLabel: 'Full Proficiency'
    },
    {
      name: (
        <span className="header-with-tooltip">
          Partial Proficiency
          <Tooltip
            skipPositioning={true}
            tooltipText="The student answered some questions for this skill correctly on at least one Pre or Post diagnostic completed in the selected timeframe."
            tooltipTriggerText={helpIcon}
          />
        </span>
      ),
      attribute: 'partialProficiency',
      width: columnWidths['224w'],
      headerClassName: 'eight-px-right-padding',
      rowSectionClassName: 'eight-px-right-padding show-overflow partial-proficiency',
      textAlign: 'left',
      noTooltip: true,
      ariaLabel: 'Partial Proficiency'
    },
    {
      name: (
        <span className="header-with-tooltip">
          No Proficiency
          <Tooltip
            skipPositioning={true}
            tooltipText="The student did not answer any questions for this skill correctly on any Pre or Post diagnostic completed in the selected timeframe."
            tooltipTriggerText={helpIcon}
          />
        </span>
      ),
      attribute: 'noProficiency',
      width: columnWidths['224w'],
      headerClassName: 'eight-px-right-padding',
      rowSectionClassName: 'eight-px-right-padding show-overflow no-proficiency',
      textAlign: 'left',
      noTooltip: true,
      ariaLabel: 'No Proficiency'
    },
    {
      name: (
        <span className="header-with-tooltip">
          No Data
          <Tooltip
            skipPositioning={true}
            tooltipText="The student has not completed the selected diagnostic in the selected timeframe."
            tooltipTriggerText={helpIcon}
          />
        </span>
      ),
      attribute: 'noData',
      width: columnWidths['224w'],
      headerClassName: 'no-data-header eight-px-right-padding',
      rowSectionClassName: 'eight-px-right-padding show-overflow no-data',
      textAlign: 'left',
      noTooltip: true,
      ariaLabel: 'No Data'
    },
  ]

  useStickyTableHeader({
    wrapperRef: tableWrapperRef,
    tableRef: tableRef,
    enabled: skillPerformances?.length > 0,
    dependencies: [Object.values(columnWidths).join('|')], // ensure resync when columnWidths change
  });

  if (!skillPerformances || additionalRequestRunning) {
    return (
      <div className="diagnostic-insights-content performance-insights">
        <Spinner />
      </div>
    )
  }

  if (!skillPerformances.length) {
    return (
      <EmptyState
        bodyText="There is no diagnostic history available for the filters selected. However, there may be history for previous versions of the selected diagnostic. Try selecting another version from the diagnostic dropdown or modifying the timeframe. Alternatively, consider assigning a diagnostic."
        headerText="No performance insights are available"
      />
    )
  }

  const renderCellTooltip = (studentPerformances, totalStudentsCount) => {
    if (!studentPerformances?.length) {
      return <DataTableChip color="grey" label={`0 of ${totalStudentsCount} students`} />
    }

    return (
      <Tooltip
        tooltipText={studentPerformances.map(studentPerformance => studentPerformance.user_name).join(', ')}
        tooltipTriggerText={<DataTableChip color="grey" label={`${studentPerformances.length} of ${totalStudentsCount} students`} />}
      />
    )
  }

  const rows = skillPerformances.map(skillPerformance => {
    const { skill_id, skill_name, full_proficiency, partial_proficiency, no_proficiency, no_data, } = skillPerformance
    const totalStudentsCount = (full_proficiency?.length || 0) + (partial_proficiency?.length || 0) + (no_proficiency?.length || 0) + (no_data?.length || 0)

    return {
      id: skill_id,
      name: skill_name,
      fullProficiency: renderCellTooltip(full_proficiency, totalStudentsCount),
      partialProficiency: renderCellTooltip(partial_proficiency, totalStudentsCount),
      noProficiency: renderCellTooltip(no_proficiency, totalStudentsCount),
      noData: renderCellTooltip(no_data, totalStudentsCount),
    }
  })

  return (
    <section className="diagnostic-insights-content performance-insights" ref={containerRef}>
      <div className="data-table-wrapper" ref={tableWrapperRef}>
        <DataTable
          headers={headers}
          reportingFormat={true}
          rows={rows}
          tableRef={tableRef}
        />
      </div>
    </section>
  )
}
export default BySkill
